import { useCallback, useState } from "react";

import { API_ENDPOINT } from "../constants/appConstants";
import { Details } from "../types/userDetails";
import { FormValues, UseOTPHandlerProps } from "../types/verification";
import { encryptPayload } from "../utils/encryptionUtils";
import { useNetwork } from "./useNetwork";

type VerifyOTPResponseData = {
  successMessage?: string;
};

export const useOTPHandler = ({
  setValue,
  setToast,
  executeRecaptcha,
  hostname,
}: UseOTPHandlerProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { fetchData } = useNetwork();

  const requestOTP = useCallback(
    async (details: Details) => {
      setIsLoading(true);
      setValue("otp", "");
      try {
        const recaptchaResult = await executeRecaptcha("requestOTP");
        const apiData = {
          username: `${details.countryCode}${details.mobileNumber}`,
          reCaptchaToken: recaptchaResult.reCaptchaToken,
          reCaptchaAction: recaptchaResult.reCaptchaAction,
        };
        const encryptedPayload = encryptPayload(apiData);
        const response = await fetchData<{ id: string }>(
          `${API_ENDPOINT(hostname).middleware.sendOTPMiddleware}`,
          "POST",
          {},
          {
            payload: encryptedPayload,
          },
        );
        if (response.data && response.data.id) {
          setValue("otpId", response.data.id);
          return true;
        }
        return false;
      } catch (error) {
        console.error("OTP Request Error:", error);
        setToast({
          show: true,
          type: "error",
          message: "Failed to send OTP. Please try again.",
        });
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [executeRecaptcha, fetchData, setValue, setToast],
  );

  const verifyOTP = useCallback(
    async (values: FormValues) => {
      const cutomErrorMessage = "Verification failed. Please try again.";
      setIsLoading(true);
      try {
        const response = await fetchData<VerifyOTPResponseData>(
          `${API_ENDPOINT(hostname).middleware.verifyOTPMiddleware}`,
          "POST",
          {},
          {
            ...values,
          },
        );

        if (response.data && response.data.successMessage) {
          setToast({
            show: true,
            type: "success",
            message: response.data.successMessage,
          });
          return { success: true };
        } else {
          let error = JSON.parse(response.error as any);
          // setToast({
          //   show: true,
          //   type: "error",
          //   message: error?.error?.message?.errorMessage || cutomErrorMessage,
          // });
          return {
            success: false,
            error: error?.error?.message?.errorMessage || cutomErrorMessage,
            status: error?.status || 500,
          };
        }
      } catch (error) {
        console.error("OTP Verification Error:", error);
        // setToast({
        //   show: true,
        //   type: "error",
        //   message: "Something went wrong.",
        // });
        return { success: false, error: cutomErrorMessage, status: 500 };
      } finally {
        setIsLoading(false);
      }
    },
    [fetchData, setToast],
  );

  return {
    requestOTP,
    verifyOTP,
    isLoading,
  };
};
